import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import DOMPurify from 'dompurify';
import { useBranding } from '../contexts/BrandingContext';
import { coreAPI } from '../lib/api';
import { useFormatters } from '../contexts/PreferencesContext';

export default function Privacy() {
  const { branding } = useBranding();
  const { formatDate: ctxFormatDate } = useFormatters();
  const [apiContent, setApiContent] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    coreAPI.getLegalContent()
      .then((res) => {
        if (res.data.privacy_content) {
          setApiContent(res.data.privacy_content);
          setUpdatedAt(res.data.privacy_updated_at);
        }
      })
      .catch(() => {
        // Silently fall back to static content
      })
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'December 2024';
    return ctxFormatDate(dateStr);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link
            to="/login"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Login
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-gray-500 mb-8">Last updated: {formatDate(updatedAt)}</p>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : apiContent ? (
            <div
              className="prose prose-gray max-w-none"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(apiContent) }}
            />
          ) : (
            <StaticPrivacyContent companyName={branding.companyName} />
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-8">
        <p className="text-sm text-gray-500">
          {branding.companyName.replace(/Portal$/i, '').trim()} Portal | Secure Business Portal
        </p>
      </div>
    </div>
  );
}

function StaticPrivacyContent({ companyName }: { companyName: string }) {
  return (
    <div className="prose prose-gray max-w-none">
      <h2 className="text-xl font-semibold mt-6 mb-3">1. Information We Collect</h2>
      <p className="text-gray-700 mb-4">
        We collect information you provide directly, including name, email address, company information, and any data you submit through the {companyName} Portal for business operations.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-3">2. How We Use Your Information</h2>
      <p className="text-gray-700 mb-4">
        We use the collected information to:
      </p>
      <ul className="list-disc pl-6 text-gray-700 mb-4">
        <li>Provide and maintain our services</li>
        <li>Process business transactions and communications</li>
        <li>Improve and personalize user experience</li>
        <li>Send important notices and updates</li>
        <li>Ensure platform security and prevent fraud</li>
      </ul>

      <h2 className="text-xl font-semibold mt-6 mb-3">3. Data Storage and Security</h2>
      <p className="text-gray-700 mb-4">
        Your data is stored securely using industry-standard encryption and security practices. We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, or destruction.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-3">4. Data Sharing</h2>
      <p className="text-gray-700 mb-4">
        We do not sell your personal information. We may share data with:
      </p>
      <ul className="list-disc pl-6 text-gray-700 mb-4">
        <li>Your organization and authorized users within your company</li>
        <li>Business partners as necessary for service provision</li>
        <li>Service providers who assist in platform operations</li>
        <li>Legal authorities when required by law</li>
      </ul>

      <h2 className="text-xl font-semibold mt-6 mb-3">5. Your Rights</h2>
      <p className="text-gray-700 mb-4">
        You have the right to:
      </p>
      <ul className="list-disc pl-6 text-gray-700 mb-4">
        <li>Access your personal data</li>
        <li>Request correction of inaccurate data</li>
        <li>Request deletion of your data (subject to legal requirements)</li>
        <li>Object to processing of your data</li>
        <li>Request data portability</li>
      </ul>

      <h2 className="text-xl font-semibold mt-6 mb-3">6. Cookies and Tracking</h2>
      <p className="text-gray-700 mb-4">
        We use cookies and similar technologies to maintain your session, remember preferences, and analyze platform usage. You can control cookie settings through your browser.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-3">7. Data Retention</h2>
      <p className="text-gray-700 mb-4">
        We retain your data for as long as your account is active or as needed to provide services. Business records may be retained longer as required by law or for legitimate business purposes.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-3">8. Changes to This Policy</h2>
      <p className="text-gray-700 mb-4">
        We may update this Privacy Policy periodically. We will notify users of significant changes through the platform or via email.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-3">9. Contact Us</h2>
      <p className="text-gray-700 mb-4">
        For privacy-related questions or to exercise your rights, please contact your system administrator or the {companyName} support team.
      </p>
    </div>
  );
}
